import React, { FC, ReactNode } from 'react';
import { useWatch } from 'react-hook-form';

import { Box, Collapse } from '@mantine/core';
import { modals } from '@mantine/modals';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';

import { Component, getComponentFieldsAtPath } from 'models';

import { ComponentDatasheetRightSection } from 'components/component-datasheet/components/ComponentDatasheetPortRightSection';
import { useDatasheetPort } from 'components/component-datasheet/hooks/use-datasheet-port';
import { useDatasheetPorts } from 'components/component-datasheet/hooks/use-datasheet-ports';
import { PortIsolatedField } from 'components/component-fields/PortIsolatedField';
import { PowerFlowField } from 'components/component-fields/PowerFlowField';
import { VoltageTypeSwitchField } from 'components/component-fields/VoltageTypeSwitchField';
import { Datasheet } from 'components/datasheet/Datasheet';

import { ComponentDatasheetPortVoltageType } from './ComponentDatasheetPortVoltageType.Team';

const ComponentDatasheetPort: FC<{ index: number; label: ReactNode }> = ({ index, label }) => {
    const type = useWatch<Pick<Component, 'type'>>({
        name: 'type',
    });

    const definition = ComponentInstanceHelpers.definition(type);
    const { showAC, showDC } = useDatasheetPort(index);

    const portFields = getComponentFieldsAtPath(type, `electrical.ports.${index}`);

    const { deletePort } = useDatasheetPorts();

    const canDelete = index + 1 > definition.ports.min;

    const openDeleteModal = () => {
        modals.openConfirmModal({
            centered: true,
            title: `Are you sure you want to delete ${label}?`,
            labels: { confirm: 'Yes, delete', cancel: "No, don't delete it" },
            confirmProps: { variant: 'light', color: 'red' },
            closeOnConfirm: false,
            onConfirm: handleDelete,
        });
    };

    const handleDelete = async () => {
        deletePort(index);
        modals.closeAll();
    };

    return (
        <Datasheet.Table
            header={{
                label,
                rightSection: (
                    <ComponentDatasheetRightSection
                        showAC={showAC}
                        showDC={showDC}
                        label={label}
                        onDelete={canDelete ? openDeleteModal : undefined}
                    />
                ),
            }}
        >
            <Datasheet.TableRow noMetadata name={`${index}.enabled`} label="This port supports">
                <VoltageTypeSwitchField index={index} py={4} px="xs" />
            </Datasheet.TableRow>

            <Collapse expanded={showAC}>
                <ComponentDatasheetPortVoltageType index={index} label={label} voltageType="AC" />
            </Collapse>
            <Collapse expanded={showDC}>
                <ComponentDatasheetPortVoltageType index={index} label={label} voltageType="DC" />
            </Collapse>
            {'powerFlowDirection' in portFields && (
                <Datasheet.TableRow name={`electrical.ports.${index}.powerFlowDirection`} label="Power Flow">
                    <Box pl="xs">
                        <PowerFlowField
                            name={`electrical.ports.${index}.powerFlowDirection`}
                            componentDefinition={definition}
                        />
                    </Box>
                </Datasheet.TableRow>
            )}
            {'isolated' in portFields && (
                <Datasheet.TableRow name={`electrical.ports.${index}.isolated`} label="Isolated">
                    <Box pl="xs">
                        <PortIsolatedField name={`electrical.ports.${index}.isolated`} />
                    </Box>
                </Datasheet.TableRow>
            )}
        </Datasheet.Table>
    );
};

export { ComponentDatasheetPort };
